import "./containers.scss";

import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Can } from "src/helpers/Auth";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import React from "react";
import { isFastDraftDeployment } from "src/reusable/Functions";
import navigation from "./_nav";
import { styled } from "@mui/material/styles";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { isFeatureEnabled } from "src/config/featureFlags";
import {
  isNavFeatureEnabled,
  onNavFeatureFlagsLoaded,
} from "src/config/posthog";
import { useGetUserSettings } from "src/views/users/SettingsService";
import { isInternalUser } from "src/helpers/internalUser";
import WorkflowNavigation from "./WorkflowNavigation";

const TheSidebar = ({
  showSidebar,
  isLargeScreen,
  setShowSidebar,
  sideBarWidth,
  collapsed,
  setCollapsed,
}) => {
  // Per-user settings gate opt-in nav entries (e.g. the agent workspace
  // tab, default off per spec §5.9).
  const { data: userSettings } = useGetUserSettings();
  const [, forceNavRefresh] = React.useState(0);
  React.useEffect(
    () => onNavFeatureFlagsLoaded(() => forceNavRefresh((n) => n + 1)),
    [],
  );

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  return (
    <Drawer
      data-testid="sidebar-container"
      variant={isLargeScreen ? "persistent" : "temporary"}
      open={showSidebar}
      onClose={() => !isLargeScreen && setShowSidebar(!showSidebar)}
      sx={{
        width: sideBarWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sideBarWidth,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <SidebarHeader>
        <Link
          to={{
            pathname: "/",
          }}
          className="the-sidebar-nav-link header"
          data-attr="sidebar:link:home-logo"
        >
          {!collapsed && (
            <img
              src={
                isFastDraftDeployment()
                  ? "/images/product-logos/Fast Draft Square 986x139.png"
                  : "/images/nuclearn-textonly-full.png"
              }
              alt="Brand Full"
              style={{ height: 32 }}
            />
          )}
          {collapsed && (
            <img
              src={
                isFastDraftDeployment()
                  ? "/images/product-logos/36x36 fast draft logo.png"
                  : "/images/n-logo-36x36.png"
              }
              alt="Brand Minimized"
              style={{ height: 30 }}
            />
          )}
        </Link>
      </SidebarHeader>
      <SidebarContent collapsed={collapsed}>
        <List>
          <NavBuilder
            items={navigation()}
            collapsed={collapsed}
            userSettings={userSettings}
          />
        </List>
      </SidebarContent>
      <SidebarFooter
        style={{
          height: "50px",
          minHeight: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <div
          data-testid="sidebar-toggle-btn"
          data-attr="sidebar:btn:toggle-collapse"
          style={{ marginRight: "-10px", marginTop: "-5px", cursor: "pointer" }}
          onClick={toggleSidebar}
        >
          {collapsed ? (
            <ArrowForwardIosIcon className="arrow-icon" />
          ) : (
            <ArrowBackIosIcon className="arrow-icon" />
          )}
        </div>
      </SidebarFooter>
    </Drawer>
  );
};

TheSidebar.propTypes = {
  showSidebar: PropTypes.bool,
  setShowSidebar: PropTypes.func,
  isLargeScreen: PropTypes.bool,
  sideBarWidth: PropTypes.number,
  collapsed: PropTypes.bool,
  setCollapsed: PropTypes.func,
};

TheSidebar.defaultProps = {
  isLargeScreen: false,
};

const SidebarHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#0F252B",
}));

const SidebarContent = styled("div")(({ collapsed }) => ({
  flexGrow: 1,
  overflowY: "auto",
  backgroundColor: "#132E31",
  color: "rgba(255, 255, 255, 0.6)",
  paddingLeft: collapsed ? 0 : "0px",
  // // using webkit for now but we are going to want to refactor this to be more general since webkit is not supported in all browsers
  // // tested - edge, firefox, chrome https://stackoverflow.com/questions/1306269/which-browsers-support-webkit-css#:~:text=Year%202021%3A%20Google%20Chrome%2C%20Safari,Only%20Internet%20Explorer%20does%20not.
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
}));

const SidebarFooter = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  position: "sticky",
  bottom: 0,
  backgroundColor: "#0F252B",
  color: "#fff",
}));

// A collapsible sidebar group (e.g. "Users & Groups"). When collapsed to the
// icon rail there's no room for the label/expander, so the children render as
// flat icon links instead.
const NavDropdown = ({ item, collapsed }) => {
  const location = useLocation();
  const children = item._children || [];
  const childActive = children.some(
    (c) => c.to && location.pathname.startsWith(c.to),
  );
  const [open, setOpen] = React.useState(childActive);

  const childLinks = children.map((child, i) => (
    <Link
      key={i}
      to={{ pathname: child.to }}
      className="the-sidebar-nav-link"
      data-testid={`sidebar-link-${child.to.slice(1).replace(/\//g, "-")}`}
      data-attr={`sidebar:link:${child.to.slice(1).replace(/\//g, "-")}`}
    >
      <ListItem
        button
        style={{
          paddingLeft: collapsed ? "35%" : "24px",
          paddingRight: collapsed ? "8px" : "0px",
          flexDirection: collapsed ? "" : "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
        className="the-sidebar-nav-link"
      >
        {child.icon}
        {!collapsed && (
          <ListItemText
            primary={child.name}
            sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
          />
        )}
      </ListItem>
    </Link>
  ));

  if (collapsed) return <>{childLinks}</>;

  return (
    <>
      <ListItem
        button
        onClick={() => setOpen((o) => !o)}
        style={{
          paddingLeft: "6px",
          paddingRight: "0px",
          alignItems: "center",
        }}
        className="the-sidebar-nav-link"
        data-testid={`sidebar-group-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      >
        {item.icon}
        <ListItemText
          primary={item.name}
          sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
        />
        {open ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>{childLinks}</List>
      </Collapse>
    </>
  );
};

NavDropdown.propTypes = {
  item: PropTypes.object,
  collapsed: PropTypes.bool,
};

/**
 * Whether a nav item is shown: internal-only, per-user-setting, PostHog
 * nav-hide, rollout-predicate and Unleash feature-flag gates, in that order.
 * Role and product (license) checks happen afterwards in `Can`.
 */
export const isNavItemVisible = (item, { internalUser, userSettings }) => {
  // Nuclearn-only entries stay hidden from every customer user, including
  // customer admins — the role check inside `Can` grants admins everything.
  if (item._internalOnly && !internalUser) {
    return false;
  }
  // Per-user opt-in entries (e.g. the agent workspace tab) hide until
  // the boolean setting is true; missing settings read as off.
  if (item._userSetting && !userSettings?.[item._userSetting]) {
    return false;
  }
  if (item._navKey && !isNavFeatureEnabled(item._navKey)) {
    return false;
  }
  // Rollout predicates (PostHog gates); re-evaluated when flags load.
  if (item._enabled && !item._enabled()) {
    return false;
  }
  if (!item._featureFlag) return true;
  return isFeatureEnabled(item._featureFlag);
};

class NavBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.handleNavItemClick = this.handleNavItemClick.bind(this);
  }

  handleNavItemClick(item) {
    if (item?.to === "/assistant") {
      const { resetDialogue } = useChatStore.getState();

      // if (typeof resetAll === "function") {
      //   resetAll();
      // }

      if (typeof resetDialogue === "function") {
        resetDialogue();
      }
    }
  }

  render() {
    const { items, collapsed, userSettings } = this.props;
    const internalUser = isInternalUser();
    // Filter items by feature flag and per-user setting
    const filteredItems = items.filter((item) =>
      isNavItemVisible(item, { internalUser, userSettings }),
    );

    return filteredItems.map((item, idx) => (
      <Can
        perform={item._roles}
        product={item._product}
        key={idx}
        yes={() =>
          item._tag === "CSidebarNavDropdown" ? (
            <NavDropdown key={idx} item={item} collapsed={collapsed} />
          ) : item._tag === "CSidebarNavTitle" ? (
            (!collapsed && (
              <ListItem
                key={idx}
                disableGutters
                style={{ paddingLeft: "6px" }}
                data-testid={`sidebar-section-${item.name.toLowerCase().replace(/ /g, "-")}`}
                data-attr={`sidebar:menu:section-${item.name.toLowerCase().replace(/ /g, "-")}`}
              >
                <ListItemText
                  sx={{ "& .MuiTypography-root": { fontSize: "11.2px" } }}
                  primary={
                    <Typography variant="caption">{item.name}</Typography>
                  }
                />
              </ListItem>
            )) ||
            (collapsed && <></>)
          ) : item._tag === "WorkflowNavigation" ? (
            // Render the WorkflowNavigation component for workflow items
            <WorkflowNavigation key={idx} collapsed={collapsed} />
          ) : (
            <div className="the-sidebar-nav-link">
              <Link
                to={{
                  pathname: item.to,
                }}
                className="the-sidebar-nav-link"
                data-testid={`sidebar-link-${item.to.slice(1).replace(/\//g, "-")}`}
                data-attr={`sidebar:link:${item.to.slice(1).replace(/\//g, "-")}`}
              >
                <ListItem
                  button
                  // Remove the component="a" and href props
                  key={idx}
                  style={{
                    paddingLeft: collapsed ? "35%" : "6px",
                    paddingRight: collapsed ? "8px" : "0px",
                    flexDirection: collapsed ? "" : "row",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                  }}
                  className="the-sidebar-nav-link"
                  onClick={() => this.handleNavItemClick(item)}
                >
                  {item.icon}
                  {!collapsed && (
                    <ListItemText
                      to={item.to}
                      primary={item.name}
                      sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}
                    />
                  )}
                  {item._children && (
                    <NavBuilder items={item._children} collapsed={collapsed} />
                  )}
                  {item.chips && item.chips}
                </ListItem>
              </Link>
            </div>
          )
        }
        no={() => null}
      />
    ));
  }
}

NavBuilder.propTypes = {
  items: PropTypes.array,
  collapsed: PropTypes.bool,
};

export default React.memo(TheSidebar);
